import { Address } from '@klear/address'
import type { StripeAccount } from '@klear/stripe'
import { toCountryCode } from '@klear/constants/countries'
import { Timestamp } from '@klear/time'
import { normalizeOrganizationName, scoreOrganizationMatch } from '@klear/organization/matching'
import type { OrganizationJson, OrganizationMeta, OrganizationStripe } from '@klear/organization/meta'

export class Organization {
  static readonly __klearClass = 'Organization'

  id: number
  name: string
  description: string
  hasKlearAccount: boolean
  meta: OrganizationMeta
  address: Address
  allianzEhid?: string
  allianzRefreshedAt?: Timestamp
  stripe: OrganizationStripe
  stripeAccountId?: string
  stripeCustomerId?: string
  private _completedKyc?: boolean

  constructor(o: Partial<OrganizationJson> = {}) {
    this.id = o.id ?? 0
    this.name = o.name ?? ''
    this.description = o.description ?? ''
    this.address = o.address ?? new Address({ country: toCountryCode(undefined) })
    this.allianzEhid = o.allianzEhid
    this.allianzRefreshedAt = o.allianzRefreshedAt
    this.meta = {
      size: o.meta?.size ?? '',
      website: o.meta?.website,
      dunsNumber: o.meta?.dunsNumber,
      lei: o.meta?.lei,
      ein: o.meta?.ein,
      vatNumber: o.meta?.vatNumber,
      parentOrganization: o.meta?.parentOrganization
    }
    this.stripe = o.stripe ?? {}
    this.hasKlearAccount = o.hasKlearAccount ?? false
    this.stripeAccountId = o.stripeAccountId ?? this.stripe.account?.id ?? undefined
    this.stripeCustomerId = o.stripeCustomerId
    this._completedKyc = o.completedKyc
  }

  toJSON(): OrganizationJson {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      address: this.address,
      allianzEhid: this.allianzEhid ?? undefined,
      allianzRefreshedAt: this.allianzRefreshedAt ?? undefined,
      meta: this.meta,
      stripe: this.stripe,
      hasKlearAccount: this.hasKlearAccount,
      stripeAccountId: this.stripeAccountId,
      stripeCustomerId: this.stripeCustomerId,
      completedKyc: this._completedKyc
    }
  }

  get isSaved(): boolean {
    return this.id > 0
  }

  setStripeAccount(account: StripeAccount): void {
    this.stripe.account = account
    if (account?.id) {
      this.stripeAccountId = account.id
    }
  }

  get country(): string {
    return this.address.country
  }
  set country(country: string) {
    this.address = this.address.withCountry(toCountryCode(country))
  }

  get addressString(): string | undefined {
    if (!this.address.line1) {
      return undefined
    }
    const addressParts = [
      this.address.line1,
      this.address.line2,
      this.address.city,
      this.address.state,
      this.address.postalCode
    ].filter(Boolean)
    return addressParts.join(', ')
  }

  get size(): string {
    return this.meta.size
  }
  set size(size: string) {
    this.meta.size = size
  }

  get website(): string {
    return this.meta.website ?? ''
  }
  set website(website: string) {
    this.meta.website = website
  }

  get dunsNumber(): string {
    return this.meta.dunsNumber ?? ''
  }
  set dunsNumber(dunsNumber: string) {
    this.meta.dunsNumber = dunsNumber
  }

  get lei(): string {
    return this.meta.lei ?? ''
  }
  set lei(lei: string) {
    this.meta.lei = lei
  }

  get ein(): string {
    return this.meta.ein ?? ''
  }
  set ein(ein: string) {
    this.meta.ein = ein
  }

  get vatNumber(): string {
    return this.meta.vatNumber ?? ''
  }
  set vatNumber(vatNumber: string) {
    this.meta.vatNumber = vatNumber
  }

  get parentOrganization(): string {
    return this.meta.parentOrganization ?? ''
  }
  set parentOrganization(parentOrganization: string) {
    this.meta.parentOrganization = parentOrganization
  }

  get completedKyc(): boolean {
    if (this._completedKyc !== undefined) {
      return this._completedKyc
    }

    const stripe = this.stripe.account
    if (!stripe) {
      return false
    }

    const termsOfServiceSigned = stripe.tos_acceptance?.date
    if (!termsOfServiceSigned) {
      return false
    }

    const requirements = stripe.requirements
    if (!requirements) {
      return false
    }

    const ignoredRequirements = ['external_account']
    const pastDueRequirements = (requirements.past_due || []).filter(
      (requirement: string) => !ignoredRequirements.includes(requirement)
    )

    const result = pastDueRequirements.length === 0
    this._completedKyc = result
    return result
  }
  set completedKyc(value: boolean | undefined) {
    this._completedKyc = value
  }

  metaForDb(): OrganizationMeta {
    return { ...this.meta }
  }

  static normalizeOrganizationName = normalizeOrganizationName
  static scoreOrganizationMatch = scoreOrganizationMatch
}
